<template>
  <div class="operationLog-container">
    <el-card class="box-card">
      <template #header>
        <div class="card-header">
          <span class="header-title">{{
            $t("message.operationLog.title")
          }}</span>          
        </div>
      </template>
 
      <div class="right-content">
        <!-- Search Form -->
        <div class="search-area">
          <el-form :inline="true">
            <el-form-item :label="$t('message.operationLog.company')" prop="company_id">
              <el-tree-select
                v-model="queryParams.companyId"
                :props="treeProps"                
                :data="companyTreeOptions"
                :load="loadTreeNode"
                lazy
                :placeholder="$t('message.operationLog.selectCompany')"
                check-strictly
                clearable
                filterable
                style="width: 200px"
                @node-click="handleNodeClick"
                node-key="id"
              />                  
            </el-form-item>

            <el-form-item :label="$t('message.operationLog.name')">
              <el-input
                v-model="queryParams.name"
                :placeholder="$t('message.operationLog.inputName')"
                clearable
                @clear="handleQuery"
                @keyup.enter="handleQuery"
                style="width: 200px"
              />
            </el-form-item>

            <el-form-item :label="$t('message.operationLog.contents')">
              <el-input
                v-model="queryParams.contents"
                :placeholder="$t('message.operationLog.inputContents')"
                clearable
                @clear="handleQuery"
                @keyup.enter="handleQuery"
                style="width: 200px"
              />
            </el-form-item>

            <el-form-item :label="$t('message.operationLog.timeRange')">
              <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="-"
                :start-placeholder="$t('message.operationLog.startTime')"
                :end-placeholder="$t('message.operationLog.endTime')"
                value-format="YYYY-MM-DD HH:mm:ss"            
              />
            </el-form-item>
            
            <el-form-item>
              <el-button type="primary" @click="handleQuery">{{
                $t("message.common.search")
              }}</el-button>
            </el-form-item>
          </el-form>
        </div>

        <!-- Data Table -->
        <div class="table-wrapper">
          <el-table
            :data="logsList"
            border
            v-loading="loading"
            style="width: 100%"
            :scroll="{ x: 'max-content' }"
            :max-height="tableHeight"
          >
            <el-table-column
              v-if="false"
              prop="id"
              :label="$t('message.operationLog.id')"
              width="80"
              fixed="left"
            />

            <el-table-column
              prop="company_name"
              :label="$t('message.operationLog.companyName')"
              width="160"
              sortable
              show-overflow-tooltip
            />

            <el-table-column
              prop="user_name"
              :label="$t('message.operationLog.name')"
              width="140"
              sortable
              show-overflow-tooltip
            />
  
            <el-table-column
              prop="contents"
              :label="$t('message.operationLog.contents')"
              width="300"
              sortable
              :show-overflow-tooltip="true"
            >
              <template #default="{ row }">
                <div class="content-cell">
                  {{ row.contents }}
                </div>
              </template>
            </el-table-column>

            <el-table-column
              prop="result"
              :label="$t('message.operationLog.result')"
              width="120"
              align="center"
            >
              <template #default="{ row }">
                <el-tag 
                  :type="getResultType(row.result)"
                  size="small"
                >
                  {{ getResultText(row.result) }}
                </el-tag>
              </template>
            </el-table-column>
 
            <el-table-column
              prop="created_at"
              :label="$t('message.operationLog.createdAt')"
              width="180"
              sortable
              fixed="right"
            >                 
              <template #default="{ row }">
                {{ row?.created_at ? formatDateTime(row.created_at) : "--" }}
              </template>                
            </el-table-column>
          </el-table> 
        </div>

        <div class="pagination-container">
          <el-pagination
            v-model:current-page="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="total"
            :page-sizes="[10,20, 30,50,100]"
            :layout="'total, sizes, prev, pager, next, jumper'"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>      
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { ElMessage } from "element-plus";
import { useI18n } from "vue-i18n";
import { getCompanyTree } from "@/api/CompanyManager";
import { getOperationlog } from "@/api/UserManager";

// 时间范围选择
const timeRange = ref([]);
const { t } = useI18n();

// Query parameters
const queryParams = ref({
  pageNum: 1,
  pageSize: 10,
  name: "",
  contents:"",
  companyId: null,
});

// Table data
const logsList = ref([]);
const loading = ref(false);
const total = ref(0);
const tableHeight = ref(500); // 固定表格高度
const companyTreeOptions = ref([]); // 添加企业树数据
// 树形配置
const treeProps = {
  value: "id",
  label: "name",
  children: "children",
};

// 计算表格高度
const calcTableHeight = () => {
  const windowHeight = window.innerHeight;
  tableHeight.value = windowHeight - 320; // 根据页面布局调整
};


// 格式化日期为接口需要的格式
const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};


const fetchData = async () => {
  
  loading.value = true;
  try {
  
    const params = {
      pageNum: queryParams.value.pageNum,
      pageSize: queryParams.value.pageSize,
      benTime: undefined,
        endTime: undefined,
      ...(queryParams.value.name && { name: queryParams.value.name }),
      ...(queryParams.value.contents && { contents: queryParams.value.contents }),      
      ...(queryParams.value.companyId && { companyId: queryParams.value.companyId }),
    };

 

    if (!(timeRange.value== undefined || timeRange.value.length !== 2) )    
    {
        // 简化判断：检查数组存在且有2个元素，且都不是null/undefined
        if (timeRange.value?.length === 2 && timeRange.value[0] && timeRange.value[1]) {
          const startTime = new Date(timeRange.value[0]);
          const endTime = new Date(timeRange.value[1]);
          
          // 额外检查日期是否有效
          if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
            params.benTime = formatDateForAPI(startTime);
            params.endTime = formatDateForAPI(endTime);
          }
        }
    }
    
        

    
    await loadLogList(params);
   } catch (error) {
    ElMessage.error(t("message.common.fetchError"));
  } finally {
    loading.value = false;
  } 
};

const loadLogList = async (params = {}) => {
  try {
    const resList = await getOperationlog(params);
    if (resList?.success) {
      logsList.value = resList.data.list;
      total.value = resList.data.totalCount;
    } else {
      ElMessage.warning(resList?.msg || t("message.operationLog.loadListFailed"));
    }
  } catch (error) {
    console.error("加载操作日志失败:", error);
    ElMessage.error(t("message.operationLog.loadListFailed"));
  } finally {
    loading.value = false;
  }
};

const loadTreeNode = async (node, resolve) => {
  try {
    await new Promise(resolve => setTimeout(resolve, 50)); 
    if (node.level === 0) {
      resolve(companyTreeOptions.value);
      return;

      // const res = await getCompanyTree({ 
      //   isGetChild: false
      // });
      // const rootNodes = transformTreeData(res.data.list || []);
      // resolve(rootNodes);
      // return;
    }

    const res = await getCompanyTree({
      isGetChild: false,
      companyId: node.data.id
    });
    
    const childNodes = transformTreeData(res.data.list || []);
    resolve(childNodes);
  } catch (error) {
    console.error('加载企业树失败:', error);
    resolve([]);
  }
};

// Transform API data to tree structure
const transformTreeData = (apiData) => {
  return apiData.map((item) => ({
    id: item.id,
    label: item.name,
    name: item.name,
    parent_id: item.parent_id,
    level: item.level,
    order_num: item.order_num,
    contact: item.contact,
    phone: item.phone,
    location: item.location,
    created_at: item.created_at,
    children: item.children ? transformTreeData(item.children) : [],
  }));
};

// Helper function to format date
const formatDateTime = (dateString) => {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    return date
      .toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
      .replace(/\//g, "-");
  } catch {
    return dateString;
  }
};

// 获取操作结果类型
const getResultType = (result) => {
  const typeMap = {
    'success': 'success',
    'error': 'danger',
    'warning': 'warning',
    'info': 'info'
  };
  return typeMap[result] || 'info';
};

// 获取操作结果文本
// 获取操作结果文本（支持国际化）
const getResultText = (result) => {
  const { t } = useI18n();
  
  const textMap = {
    '1': t('message.common.success'),
    'success': t('message.common.success'),
    'error': t('message.common.failed'),
    '0': t('message.common.failed'),
    'warning': t('message.common.warning'),
    'info': t('message.common.info'),
    '3': t('message.common.success'),
  };
  return textMap[result] || result;
};


const handleNodeClick = async (nodeData) => {
  queryParams.value.companyId = nodeData.id;
  queryParams.value.pageNum = 1;
  fetchData();
};

const handleQuery = () => {
  queryParams.value.pageNum = 1;
  fetchData();
};

const handleSizeChange = (val) => {
  queryParams.value.pageNum = 1;
  queryParams.value.pageSize = val;
  fetchData();
};

const handleCurrentChange = (val) => {
  queryParams.value.pageNum = val;
  fetchData();
};


// // 修复：生成符合 value-format 的字符串时间
// const setDefaultTimeRange = () => {
//   const today = new Date();
//   // 当天开始时间：00:00:00
//   const startOfDay = new Date(today.setHours(0, 0, 0, 0));
//   // 当天结束时间：23:59:59
//   const endOfDay = new Date(today.setHours(23, 59, 59, 999));

//   // 格式化函数：转为 "YYYY-MM-DD HH:mm:ss" 字符串
//   const formatToStr = (date) => {
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     const hours = String(date.getHours()).padStart(2, '0');
//     const minutes = String(date.getMinutes()).padStart(2, '0');
//     const seconds = String(date.getSeconds()).padStart(2, '0');
//     return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
//   };

//   // 赋值符合格式的字符串数组，而非 Date 对象
//   timeRange.value = [formatToStr(startOfDay), formatToStr(endOfDay)];
// };

// 初始化加载数据
const initData = async () => {
  try {
    loading.value = true;
    // 并行加载企业树和日志数据
    await Promise.all([
      loadCompanyTree(),
      fetchData()
    ]);
  } catch (error) {
    ElMessage.error(error || t("message.common.fetchError"));
  } finally {
    loading.value = false;
  }
};

// 加载企业树数据
const loadCompanyTree = async () => {
  try {
    const res = await getCompanyTree({ 
      isGetChild: false
    });
    if (res?.success) {
      companyTreeOptions.value = transformTreeData(res.data.list || []);

      queryParams.value.companyId = companyTreeOptions.value[0].id;
    } else {
      ElMessage.warning(res?.msg || t("message.common.loadFailed"));
    }
  } catch (error) {
    console.error('加载企业树失败:', error);
    ElMessage.error(t("message.common.loadFailed"));
  }
};


onMounted(async () => {
  calcTableHeight();
  window.addEventListener('resize', calcTableHeight);
  await initData();
  //setDefaultTimeRange();    
});
</script>

<style scoped lang="scss">
.operationLog-container {
  padding: 20px;

  .box-card {
    border-radius: 4px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-title {
        font-size: 16px;
        font-weight: bold;
      }
    }
  }

  .right-content {
    .search-area {
      margin-bottom: 20px;

      :deep(.el-form-item) {
        margin-bottom: 0;
        margin-right: 16px;
      }
    }

    .table-wrapper {
      width: 100%;
      margin-bottom: 20px;
      position: relative;

      :deep(.el-table) {
        width: 100% !important;
        border: 1px solid #ebeef5;
        border-radius: 4px;

        // 固定表头样式
        .el-table__header-wrapper {
          position: sticky;
          top: 0;
          z-index: 10;
        }

        // 表格单元格样式
        th, td {
          border-right: 1px solid #ebeef5;
          border-bottom: 1px solid #ebeef5;
          padding: 8px 12px;
        }

        // 内容列样式
        .content-cell {          
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 1.4;
        }

        // 固定列阴影效果
        .el-table__fixed,
        .el-table__fixed-right {
          box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
        }
      }

      .empty-state {
        padding: 40px 0;
        text-align: center;
        background: #fff;
        border: 1px solid #ebeef5;
        border-radius: 4px;
      }
    }

    .pagination-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      margin-top: 20px;
    }
  }
}

// 响应式设计
@media (max-width: 768px) {
  .operationLog-container {
    padding: 10px;
    
    .right-content {
      .search-area {
        :deep(.el-form-item) {
          display: block;
          margin-right: 0;
          margin-bottom: 10px;
          
          .el-form-item__label {
            width: 100%;
            text-align: left;
          }
        }
      }
    }
  }
}

// 滚动条样式优化
:deep(.el-table__body-wrapper) {
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #c1c1c1;
    border-radius: 4px;
    
    &:hover {
      background-color: #a8a8a8;
    }
  }
  
  &::-webkit-scrollbar-track {
    background-color: #f1f1f1;
    border-radius: 4px;
  }
}
</style>